import React, { useEffect, useState } from "react";
import { Activity, Timer, Layers, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Compact "how's the plant running" strip for the admin vetting cockpit.
 * Polls /api/admin/pulse every 15s and shows three vital signs inline:
 *   • Queue depth  (pending / open)
 *   • Scanner     (last cycle time, reviewed → queued, cap)
 *   • LLM         (last / avg latency, 5-min call count, error indicator)
 *
 * Kept intentionally small — dashboard real estate is precious and this
 * lives above the vetting queue, so every glyph has to earn its width.
 */
export default function PulseStrip() {
  const [pulse, setPulse] = useState(null);
  const [error, setError] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/pulse");
      setPulse(r.data);
      setError(false);
    } catch (e) {
      setError(true);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (error) {
    return (
      <div
        className="mb-5 flex items-center gap-2 rounded-md px-3 py-2 border border-red-300 bg-red-50 text-red-800 text-xs"
        data-testid="pulse-strip-error"
      >
        <AlertTriangle className="w-4 h-4" />
        Pulse unavailable — check backend logs.
      </div>
    );
  }
  if (!pulse) {
    return (
      <div
        className="mb-5 h-10 rounded-md bg-neutral-100 border border-mb-border animate-pulse"
        data-testid="pulse-strip-loading"
      />
    );
  }

  const q = pulse.queue || {};
  const s = pulse.scanner || {};
  const l = pulse.llm || {};

  const scannerStale = !s.at;
  const scannerAgeMs = s.at ? Date.now() - new Date(s.at).getTime() : null;
  const llmDegraded = l.p95_ms && l.p95_ms > 8000;
  const llmErrRecent = l.last_error_ago_s != null && l.last_error_ago_s < 300;

  return (
    <div
      className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md px-4 py-3 bg-neutral-900 border border-mb-border text-white text-sm font-mono-data shadow-md"
      data-testid="pulse-strip"
    >
      <span className="uppercase tracking-widest text-[11px] text-mb-emerald font-sans font-bold">
        Pulse
      </span>

      {/* Queue */}
      <span className="flex items-center gap-2" title="Alerts awaiting vet / live positions">
        <Layers className="w-4 h-4 text-mb-emerald" />
        <span className="text-white/80 font-semibold">Queue</span>
        <span className="text-white font-bold text-base" data-testid="pulse-queue-pending">
          {q.pending_review ?? "–"}
        </span>
        <span className="text-white/70">pending</span>
        <span className="text-white/40 mx-1">·</span>
        <span className="text-white font-bold" data-testid="pulse-queue-open">{q.approved_open ?? "–"}</span>
        <span className="text-white/70">open</span>
      </span>

      <span className="w-px h-5 bg-white/20" aria-hidden />

      {/* Scanner */}
      <span
        className="flex items-center gap-2"
        title={
          scannerStale
            ? "No scan since pod boot yet"
            : `Last scan ${new Date(s.at).toLocaleTimeString()} · ${s.cycle_ms}ms`
        }
      >
        <Activity className="w-4 h-4 text-mb-emerald" />
        <span className="text-white/80 font-semibold">Scanner</span>
        {scannerStale ? (
          <span className="text-white/70 italic" data-testid="pulse-scanner-idle">warming up…</span>
        ) : (
          <>
            <span className="text-white font-bold" data-testid="pulse-scanner-reviewed">{s.reviewed ?? 0}</span>
            <span className="text-white/70">reviewed →</span>
            <span className="text-mb-emerald font-bold text-base" data-testid="pulse-scanner-queued">{s.queued ?? 0}</span>
            <span className="text-white/70">queued</span>
            <span className="text-white/60">(cap {s.cap ?? "–"})</span>
            <span className="text-white/40 mx-1">·</span>
            <span className="text-white/90" data-testid="pulse-scanner-cycle">{Math.round((s.cycle_ms ?? 0) / 100) / 10}s</span>
            {scannerAgeMs != null && (
              <span className="text-white/60 ml-1">
                ({Math.floor(scannerAgeMs / 60000)}m ago)
              </span>
            )}
          </>
        )}
      </span>

      <span className="w-px h-5 bg-white/20" aria-hidden />

      {/* Grade distribution — S/A/B tally over the last 20 scanner ticks. */}
      <span
        className="flex items-center gap-2"
        title={
          (s.grade_distribution && (s.grade_distribution.S + s.grade_distribution.A + s.grade_distribution.B) > 0)
            ? `Last 20 ticks · ${s.grade_distribution.reviewed} reviewed · ${s.grade_distribution.blocked} blocked at conviction gate`
            : "No grade data yet — waiting for scanner ticks"
        }
      >
        <span className="text-white/80 font-semibold">Grades</span>
        <span className="text-mb-emerald font-bold" data-testid="pulse-grade-S">
          S:{s.grade_distribution?.S ?? 0}
        </span>
        <span className="text-white/40">·</span>
        <span className="text-white font-bold" data-testid="pulse-grade-A">
          A:{s.grade_distribution?.A ?? 0}
        </span>
        <span className="text-white/40">·</span>
        <span className="text-white/70" data-testid="pulse-grade-B">
          B:{s.grade_distribution?.B ?? 0}
        </span>
        <span className="text-white/40">·</span>
        <span className="text-red-300 font-bold" data-testid="pulse-grade-blocked">
          blk:{s.grade_distribution?.blocked ?? 0}
        </span>
      </span>

      <span className="w-px h-5 bg-white/20" aria-hidden />

      {/* LLM */}
      <span
        className={`flex items-center gap-2 ${
          llmDegraded || llmErrRecent ? "text-amber-200" : ""
        }`}
        title={
          l.count_5m
            ? `${l.count_5m} calls in last 5m · avg ${l.avg_ms}ms · p95 ${l.p95_ms}ms`
            : "No LLM calls in the last 5m"
        }
      >
        <Timer className={`w-4 h-4 ${llmDegraded || llmErrRecent ? "text-amber-200" : "text-mb-emerald"}`} />
        <span className={`font-semibold ${llmDegraded || llmErrRecent ? "text-amber-100" : "text-white/80"}`}>LLM</span>
        {l.last_ms == null ? (
          <span className="text-white/70 italic" data-testid="pulse-llm-idle">idle</span>
        ) : (
          <>
            <span className="text-white font-bold text-base" data-testid="pulse-llm-last">{Math.round(l.last_ms)}ms</span>
            <span className="text-white/70">last</span>
            {l.avg_ms != null && (
              <>
                <span className="text-white/40 mx-1">·</span>
                <span className="text-white font-bold" data-testid="pulse-llm-avg">{Math.round(l.avg_ms)}ms</span>
                <span className="text-white/70">avg (5m, {l.count_5m})</span>
              </>
            )}
            {llmErrRecent && (
              <span className="ml-2 text-amber-200 font-bold" data-testid="pulse-llm-error">
                err {l.last_error_ago_s}s ago
              </span>
            )}
          </>
        )}
      </span>
    </div>
  );
}
